
import { Model } from '../../model/model';
import { FreedataEngine as BaseFreedataEngine, BaselineEngine, EM_ACTION, FIELD_KIND, FeiKeyDef, KEY_KIND, WidgetLine, WizConstants, FreedataUpdatePack, Triggers, FormulaCoreEngine, MeasMode, MeasureCoreEngine, ComputeResult, IBaselineValue, COMPUTE_MODE } from '@wizeoo/akm-compute-lib';
import { FreedataArgsVO } from '../../vo/core/freedataargs.vo';
import { Baseline, BaselineValue, BusinessException, DataPack, EMPackEntry, EntityModificationPack, EntityModificationTrace, FreeData, FreeDataArgs, FreeDataLevel, FreeDataValue, Measure, VisionKey } from '@wizeoo/akm-message-lib';
import { FreeDataVO } from '../../vo/core/freedata.vo';
import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { FreedataLevelVO } from '../../vo/core/freedatalevel.vo';
import { TranslatableLabelEntryVO } from '../../vo/common/translation/translatable-label-entry.vo';
import { ViewData } from '../../model/view.data';
import { ICellRendererParams } from 'ag-grid-community';
import { UpdateEntry } from '../../command/core/sync/sync.service';
import { BASIC_ENTITY_TYPE } from '../../model/enums';
import { cloneDeep } from 'lodash-es';
import { TranslatableLabelVO } from '../../vo/common/translation/translatable-label.vo';
import { WIZ_COMMAND_STATE, WizDateUtils, WizMathUtils, WizStringUtils } from '@wizeoo/akm-core-lib';
import { IFreedataWidget } from '../widgets/i-widget';
import { EMEngine } from '../entities/entity-modification.engine';
import { WizIcon, WizPopUp, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { CommitCmd } from '../../command/em/commit.cmd';
import { FeiVO } from '../../vo/workingdata/fei.vo';

import { TriggerMatrixItem } from '../computer/freedata.formula.core.engine';
/**
 * objet de configuration des args sur un FD
 */
export interface ArgConfig {
    code: string, // code de l'arg
    label: string, // libellé de l'arg
    order: number, // son ordre
    datakind: string, // son type
    size?: number // sa taille par défaut
}

/**
 * gestion des FreeDatas pour le moteur
 */
export class FreedataEngine extends BaseFreedataEngine {
 
    constructor(model: Model) {
        super(model);
    }

    public get model(): Model {
      return this._model as Model;
    }


    /* ################################################
        les constructeurs (pour obtenir le bon type)
    ################################################ */

    public newFreedataVO(source?: FreeData): FreeDataVO {
        return new FreeDataVO(this.model, source);
    }

    public newFreedataValueVO(source?: FreeDataValue): FreedataValueVO {
        return new FreedataValueVO(this.model, source);
    }

    public newFreedataLevelVO(source?: FreeDataLevel): FreedataLevelVO {
        return new FreedataLevelVO(this.model, source);
    }

    public newFreedataArgsVO(source?: FreeDataArgs): FreedataArgsVO {
        return new FreedataArgsVO(this.model, source);
    } 

    /* ################################################
        les accesseurs (pour obtenir le bon type)
    ################################################ */

    public getFreeDataCollection(): FreeDataVO[] {
        return this.model.freeDataCollection ? this.model.freeDataCollection as FreeDataVO[] : null;
    }

    public getFreedataMap(): Map<string, FreeDataVO> {
        return this.model.freedataMap ? this.model.freedataMap as Map<string, FreeDataVO> : null;
    }

    /**
     * Recherche d'un free data par code
     * @param pCode : Code du free data recherché
     * @return FreeDataVO
     */
    public getFreeData(pCode: string): FreeDataVO {
        return this.getFreedataMap()?.get(pCode);
    }

    /**
     * Return le FDV associé au code demandé. Retourne null si aucune correspondance
     * @param pFdvCode le code du FD
     * @param pCode Le code du FDV
     */
    public getFreedataValue(pFDCode: string, pCode: string): FreedataValueVO {
       return super.getFreedataValue(pFDCode, pCode) as FreedataValueVO;
        // const freeData: FreeDataVO = this.getFreeData(pFDCode.split(WizConstants.CONTEXT_SEPARATOR)[0]); // suppression du niveau
        // let result: FreedataValueVO = null;
        // if (!!freeData) {
        //     result = freeData.getFreeDataValue(pCode);
        //     if (result === undefined) {
        //         result = null;
        //     }
        // }
        // return result;
    }

    /**
     * Return les FDVs associés au code PARENT demandé. Retourne null si aucune correspondance
     * @param pFdvCode le code du FD
     * @param pCode Le code du FDV PARENT
     */
    public getFreedataValueByParent(pFDCode: string, pCode: string): FreedataValueVO[] {
        const freeData: FreeDataVO = this.getFreeData(pFDCode.split(WizConstants.CONTEXT_SEPARATOR)[0]); // suppression du niveau
        let result: FreedataValueVO[] = null;
        if (!!freeData) {
            result = freeData.getFreeDataValueByParent(pCode);
            if (result === undefined) {
                result = null;
            }
        }
        return result;
    }

    /**
     * Return le FDV associé au shortCode demandé. Retourne null si aucune correspondance ou multiple
     * @param pFdvCode le code du FD
     * @param pCode Le code du FDV
     */
    public getFreedataValueByShortCode(pFDCode: string, pShortCode: string): FreedataValueVO {
        const freeData: FreeDataVO = this.getFreeData(pFDCode.split(WizConstants.CONTEXT_SEPARATOR)[0]); // suppression du niveau
        if (!!freeData) {
            const fdv: FreedataValueVO[] = freeData.getFreeDataValueByShortCode(pShortCode);
            if (fdv && fdv.length === 1) {
                return fdv[0];
            }
        }
        return null;
    }

  /* ################################################
    génération des args pour les propriétés basiques
  ################################################ */

    /**
     * map des champs de base = ArgConfig
     */
    private _basicFieldSet: Map<string, ArgConfig> = null;
  
    /** retour les args basiques pour les FD/ FDVs */
    public basicFieldSet(): Map<string, ArgConfig> {
      if (!this._basicFieldSet) {
        let arr: ArgConfig[] = [];

        arr.push(this.argBuilder('shortCode', '$.field.shortcode.$', 1, FIELD_KIND.STRING, 80));
        arr.push(this.argBuilder( 'parentCode', '$.field.parentcode.$', 2, FIELD_KIND.STRING, 80));
        arr.push(this.argBuilder( 'label', '$.field.label.$', 3, FIELD_KIND.TRS_LABEL, 250));
        arr.push(this.argBuilder( 'valor', '$.field.value.$', 4, FIELD_KIND.STRING, 60));
        arr.push(this.argBuilder( 'externalCode', '$.field.externalcode.$', 5, FIELD_KIND.STRING, 80));
        arr.push(this.argBuilder( 'externalParentCode', '$.field.externalparentcode.$', 6, FIELD_KIND.STRING, 80));
        arr.push(this.argBuilder( 'icon', '$.field.image.$', 7, FIELD_KIND.BINARY, 50));
        arr.push(this.argBuilder( 'description', '$.field.description.$', 8, FIELD_KIND.TRS_LABEL, 250));
        arr.push(this.argBuilder( 'code', '$.field.code.$', 9, FIELD_KIND.STRING, 80));
        arr.push(this.argBuilder( 'createBy', '$.field.createby.$', 10, FIELD_KIND.STRING, 250));
        arr.push(this.argBuilder( 'updateBy', '$.field.updateby.$', 11, FIELD_KIND.STRING, 250));
        arr.push(this.argBuilder( 'parentShortCode', '$.field.parent.shortcode.$', 12, FIELD_KIND.STRING, 80));
        arr.push(this.argBuilder( 'createTime', '$.field.createtime.$', 13, FIELD_KIND.DATE, 80));
        arr.push(this.argBuilder( 'updateTime', '$.field.updatetime.$', 14, FIELD_KIND.DATE, 80));
        arr.push(this.argBuilder( 'startDate', '$.field.startdate.$', 15, FIELD_KIND.DATE, 80));
        arr.push(this.argBuilder( 'endDate', '$.field.enddate.$', 16, FIELD_KIND.DATE, 80));
        arr.push(this.argBuilder( 'deprecated', '$.field.deprecated.$', 17, FIELD_KIND.BOOLEAN, 40));
        arr.push(this.argBuilder( 'restrict', '$.field.restrict.$', 18, FIELD_KIND.STRING, 250));
        arr.push(this.argBuilder( 'order', '$.common.order.$', 19, FIELD_KIND.NUMBER, 80));
       
        this._basicFieldSet = new Map(arr.map(item => [item.code, item]));
      }
      return this._basicFieldSet;
    }
  
    /**
     * construction d'un ArgConfig depuis une série de params
     * @param code 
     * @param label 
     * @param order 
     * @param datakind 
     * @param size 
     * @returns 
     */
    private argBuilder(code: string, label: string, order: number, datakind: string, size?: number): ArgConfig {
       return {code, label, order, datakind, size}; 
    }

    /**
     * génère les args pour les champs basiques du Freedata
     * pour rendu (ajouter dynamiquement les properties des FDVs)
     * @param freeDataCode du FD
     * @return FreedataArgsVO[]
     */
    public buildBasicArgs(freeDataCode: string): FreedataArgsVO[] {
        const result: FreedataArgsVO[] = [];
        let fdArg: FreedataArgsVO;
        [...this.basicFieldSet().values()].forEach((arc: ArgConfig) => {
            fdArg = this.newFreedataArgsVO();
            fdArg.freeDataCode = freeDataCode;
            fdArg.code = arc.code;
            fdArg.constantLabel = arc.label;
            fdArg.dataType = arc.datakind;
            fdArg.order = arc.order;
            fdArg.defaultSize = arc.size;
            result.push(fdArg);
        });
        return result;
    }

  /* ################################################
    gestion des levels
  ################################################ */

    /**
   * pré configuration d'un FreedataLevelVO
   * @return FreedataLevelVO
   */
    public createFreedatalevel(freedata: FreeDataVO, level: number): FreedataLevelVO {
        const freedataLevel = this.newFreedataLevelVO();
        freedataLevel.freedataCode = freedata.code;
        freedataLevel.businessUnitCode = freedata.businessUnitCode;
        freedataLevel.domain = freedata.domain;
        freedataLevel.level = level;
        freedataLevel.code = 'LVL' + level;
    
        let levelLabel: string = '';
        if (level === 1) {
          levelLabel = freedata.trsLabel;
        } else {
          levelLabel = freedata.trsLabel + ' LVL' + level;
        }
    
        let entry: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
        entry.languageCode = this.model.buLocaleCode; // langue de la BU
        entry.label = levelLabel;
        freedataLevel.labelVO.addEntry(entry);
        const currentLanguage: string = this.model.currentLocaleCode;
        if (currentLanguage !== entry.languageCode) {
          // langue en cours de saisie
          entry = new TranslatableLabelEntryVO();
          entry.languageCode = currentLanguage;
          entry.label = levelLabel;
          freedataLevel.labelVO.addEntry(entry);
        }
        return freedataLevel;
      }

  /** accesseur sur un FDL par son level */
  public getFreeDataLevel(freedata: FreeDataVO, pLevel: number): FreedataLevelVO {
    if (!freedata.levelsVOMap || freedata.levelsVOMap.size === 0) {
      // patch, pour FD wild non inits
      this.addFreeDataLevel(freedata, this.createFreedatalevel(freedata, 1));
    }
    return freedata.levelsVOMap.get(pLevel);
  }

  /**
   * Ajout d'un niveau
   * @param pLevel Vo ajouté
   */
  public addFreeDataLevel(freedata: FreeDataVO, pLevel: FreedataLevelVO): void {
    freedata.levelsVO.push(pLevel);
    if (!freedata.source.freeDataLevels) {
        freedata.source.freeDataLevels = {};
    }
    freedata.source.freeDataLevels[pLevel.level] = pLevel.source;
  }

  /**
   * Suppression d'un niveau (tous ses enfants disparus)
   * @param pLevel Vo ajouté
   */
  public removeFreeDataLevel(freedata: FreeDataVO, pLevel: FreedataLevelVO): void {
    const index: number = freedata.levelsVO.indexOf(pLevel);
    if (index >= 0) {
        freedata.levelsVO.splice(index, 1);
    }
    delete freedata.source.freeDataLevels[pLevel.level];
  }

  /**
   * génère un PKD pour les FreedataFields, pour compatibilité avec les PivotField et les FilterObj
   */
  public  getPkdFromFD(fei: FeiVO, fdCodeLevel: string): FeiKeyDef {
    const visKey: VisionKey = new VisionKey();
    visKey.code = fdCodeLevel; // .split(ViewData.CONTEXT_SEPARATOR)[0];
    visKey.level = Number(fdCodeLevel.split(ViewData.CONTEXT_SEPARATOR)[1]);
    return BaselineEngine.buildFeiKeyDef(fei, KEY_KIND.KEY, visKey).setParams(true, true);
  }

  /**
   * pour un FD donné, retourne la liste des champs possibles
   * @param code le code du FD, simple
   * @returns une collection de fakeVO
   */
  public getArgsFromFD(code: string): FreedataArgsVO[] {
    const fd: FreeDataVO = this.model.freedataEngine.getFreeData(code);
    if (fd) {
      return fd.allFieldsAsArgs;
    }
    return null;
  }

  /**
   * pour un FD donné, et un code de champ, retourne le arg
   * @param codeFD
   * @param field le champ
   * @returns le argVO
   */
  public getArgFromFD(codeFD: string, field: string): FreedataArgsVO {
    const args: FreedataArgsVO[] = this.getArgsFromFD(codeFD);
    if (args) {
      return new Map(args.map(item => [item.code, item])).get(field);
    }
    return null;
  }

  /**
   * récupération de la liste des FDV d'un FD, pour un niveau donné
   *
   * @param freedataCode, code du freedata contenant les FD sous la forme CODE#Level
   * @return la liste des FDV
   */
  public getFreedataValuesByLevel(freedataCode: string): FreedataValueVO[] {
    const parts: string[] = freedataCode.split(ViewData.CONTEXT_SEPARATOR); // on split sur #
    // récupération du freedata dans le model
    const freedata = this.model.freedataEngine.getFreeData(parts[0]);
    if (freedata && freedata.getValuesVO()) {
      return freedata.getValuesVO().filter((fdv: FreedataValueVO) => fdv.level === Number(parts[1]));
    }
    return [];
  }

  /**
   * pour un fkd de type séquence, retourne la prochaine disponible
   * 
   * @param fei fei cible pour collecte des soeurs
   * @param fkd séquence
   * @returns 
   */
  public getNextSequence(fei: FeiVO, fkd: FeiKeyDef): string {
    let result: string = null;
    // collecte de mes soeurs
    let sisters: WidgetLine[] = fei.getAllLines();
    // let sisters: Map<string, WidgetLine> = fei.getWidgetLinesByLevel(widgetLine.scen, widgetLine.level, widgetLine.bl.pkey);
    let index: number = fkd.indexInKey;

    let max: number = 1000;
    let sisterVal: number;
    sisters?.forEach((sister: WidgetLine) => {
      if (!sister.bl.splittedKey) {
        sister.bl.splittedKey = sister.bl.key.split(WizConstants.KEY_SEPARATOR);
      }
      sisterVal = parseInt(sister.bl.splittedKey[index], 16);
      if(sisterVal > max) {
        max = sisterVal;
      }
    });
    max++;
    result = max.toString(16);
    return result;
  }

  /**
   * Retourne la liste des free data du model
   * @return FreeDataVO[]
   */
  public getFreeDataList(): FreeDataVO[] {
    return [...this.getFreedataMap()?.values() || []];
  }

  /** gestion des restrictions de saisie (fonction de restriction sur codifs) */
  public checkEditAvailable(event: ICellRendererParams): boolean {

    let result: boolean = true;

    const fdv: FreedataValueVO = event.data;
    const colDef: any = event.colDef;

    const fdavo: FreedataArgsVO = fdv.getParentFDVO().getArgsVOMap().get(colDef.fdvfield);

    // contrôle des restrictions
    if (!!fdavo.restrict) { // en cas de validateur
      const neoExp = fdavo.restrict;

      result = this.model.freedataFormulaCoreEngine.checkFreedataExp(fdv, neoExp, colDef.fei);
    }
    return result;
  }

  /* ################################################
    synchro sur retour de commit composite
  ################################################ */

  /** 
   * ajout à un updatePack pour les freedatas 
   * Création si nécessaire
   * @param packMap Map<string, FreedataUpdatePack> par code FD = FreedataUpdatePack
   * @param fdv
   * @param operation
   * @returns le FreedataUpdatePack mis à jour
   */
  private generateFreedataUpdatePack(packMap: Map<string, FreedataUpdatePack>, fdv: FreeDataValue, operation: string): FreedataUpdatePack {
    let result: FreedataUpdatePack = packMap.get(fdv.freeDataCode) || null;
    if(!result) { // ajout à la map si nécessaire
      result = { 
        freedataCode: fdv.freeDataCode,
        toRemoveFDV: new Map(),
        toUpdateFDV: new Map(),
        toCreateFDV: new Map()};
        packMap.set(fdv.freeDataCode, result);
    }
    
    switch(operation) {
      case EM_ACTION.DELETE:
        result.toRemoveFDV.set(fdv.code, fdv);
        break;
      case EM_ACTION.CREATE:
        if(!result.toRemoveFDV.has(fdv.code)) { // la suppression prime
          result.toCreateFDV.set(fdv.code, fdv);
        }
        break;
      case EM_ACTION.UPDATE:
        if(!result.toRemoveFDV.has(fdv.code)) { // la suppression prime
          result.toUpdateFDV.set(fdv.code, fdv);
          let real = this.getFreedataValueByShortCode(fdv.freeDataCode, fdv.code);
          if(real) {
            // maj du modèle 
            real.source = fdv;
          }

        }
        break;
    }
    
    return result;
  }

  /**
   * TODO TODO revoir l'utilité de ce bout de code
   * retour de service composite
   * refresh des collections
   * @param sourcePack 
   * @returns une map de modif FDCode = FreedataUpdatePack pour préparation des modifs de matrice
   */
  public mergeFromCompositeCommit(sourcePack: DataPack): Map<string, FreedataUpdatePack> {
     
    // TODO TODO possible FD multiples
    // prendre inFreedata && remoteFD et boucler

    // modifs par FD
    let result: Map<string, FreedataUpdatePack> = new Map();
    
    // préservation des sources FDV, clé = code FD
    let sourceFDMap: Map<string, FreeDataVO> = new Map();
    let sourceFDVMap: Map<string, {[key: string]: FreeDataValue}> = new Map();

    let realVO: FreeDataVO;
    let realVOSource: {[key: string]: FreeDataValue};

    if(sourcePack.inFreedata) {
      // l'objet FD manipulé (form de création mixte ou widget de type FD) 
      realVO = this.model.freedataEngine.getFreeData(sourcePack.inFreedata.code);
      if(realVO) {
        realVO.source = sourcePack.inFreedata;// on injecte le FD correct dans la source
        realVOSource = cloneDeep(realVO.source.freeDataValues);
        sourceFDMap.set(realVO.code, realVO);
        sourceFDVMap.set(realVO.code, realVOSource);
        realVO.resetCollections(); // on rince ses collections 
      }
    }
    
    if(sourcePack.remoteFreedata?.length > 0) {
      sourcePack.remoteFreedata.forEach(fd => {
        realVO = this.model.freedataEngine.getFreeData(fd.code);
        if(realVO) {
          realVOSource = cloneDeep(fd.freeDataValues);
          realVO.source = fd;// on injecte le FD correct dans la source
          sourceFDMap.set(realVO.code, realVO);
          sourceFDVMap.set(fd.code, realVOSource);
          realVO.resetCollections(); // on rince ses collections 
        }
      });
    }
   
    let goodMap: {[key: string]: FreeDataValue};

    if(!!sourcePack.deletedFreedataAndValues) { // gestion des deleted sur FDV
         
      sourcePack.deletedFreedataAndValues.forEach((emt: EntityModificationTrace) => { 
        if(emt.clas === BASIC_ENTITY_TYPE.FREEDATAVALUE) { 
          
          // TODO TODO ajouter le FDV si pas présent dans la map
          
          // patch LMA
          goodMap = sourceFDVMap.get(emt.key);
          if(goodMap) {
            let existFDV: FreeDataValue = goodMap ? goodMap[emt.mkey] : null; //sourceFDV[emt.mkey];
            if(!existFDV) {
              existFDV = new FreeDataValue();
              existFDV.freeDataCode = realVO.code;
              existFDV.code = emt.mkey;
              existFDV.operation = EM_ACTION.DELETE;
            }

            this.generateFreedataUpdatePack(result, existFDV, EM_ACTION.DELETE);
            delete goodMap[emt.mkey];
            // this.model.cellMatrixEngine.removeFDVCell(emt.key, emt.mkey);
          }
          // if(emt.key === realVO.code) { // il est à moi
          //   delete sourceFDV[emt.mkey];
          // } 
          
        }
      });
    }
 
    // le pack complet à mettre à jour POUR CE FD
    let fdvPack: FreeDataValue[] = []; 
      
    if (!!sourcePack.inFreedataValues) { // les miens
      fdvPack = fdvPack.concat(sourcePack.inFreedataValues);
    }
    if (!!sourcePack.remoteFreedataValues) { // les remotes sur même périmètre
      fdvPack = fdvPack.concat(sourcePack.remoteFreedataValues);
    }

    fdvPack.forEach((fdv: FreeDataValue) => { // on balaie pour alimenter les collections

      // ajout dans le sourceMap pour reset si nécessaire (modif d'autres FDV par autre personne, modif multiple sur linkedRef)
      if(!sourceFDMap.has(fdv.freeDataCode)) {
        realVO = this.model.freedataEngine.getFreeData(fdv.freeDataCode);
        if(realVO) {
          sourceFDMap.set(realVO.code, realVO);
          sourceFDVMap.set(realVO.code, realVO.source.freeDataValues);
          realVO.resetCollections(); // on rince ses collections 
        }
      }
       
      switch (fdv.operation) {
        case EM_ACTION.DELETE:
            this.generateFreedataUpdatePack(result, fdv, EM_ACTION.DELETE);
            goodMap = sourceFDVMap.get(fdv.freeDataCode);
            if(goodMap) {
              delete goodMap[fdv.code];
              // this.model.cellMatrixEngine.removeFDVCell(fdv.freeDataCode, fdv.code);
            }
            
            // if(fdv.freeDataCode === realVO.code) { // c'est l'un des miens
            //   delete sourceFDV[fdv.code];
            // }   
          break;
        case EM_ACTION.CREATE: 
        case EM_ACTION.UPDATE: 
        
          this.generateFreedataUpdatePack(result, fdv, fdv.operation);
          goodMap = sourceFDVMap.get(fdv.freeDataCode);
          if(goodMap) {
            goodMap[fdv.code] = fdv;
            // géré par l'update sur dataengine.recomputeFreedatas
            // this.model.cellMatrixEngine.forgetFDVCell(fdv.freeDataCode, fdv.code);
            // delete goodMap[fdv.code];
          }
          
          // if(fdv.freeDataCode === realVO.code) { // c'est l'un des miens
          //   sourceFDV[fdv.code] = fdv;
          // } 
          break;
      }
       
    });

    [...sourceFDMap.keys()].forEach(fdcode => {
      sourceFDMap.get(fdcode).source.freeDataValues = sourceFDVMap.get(fdcode);
      sourceFDMap.get(fdcode).resetUpdateMap();
    });


    // realVO.source.freeDataValues = sourceFDV; // on remet les FDVs en place
    // realVO.resetUpdateMap(); // on rince sa map de modifs 
    return result;
  }
  
  /**
   * Clonage d'un FreeDataVO
   * @param pSourceVO L'objet source
   */
  public cloneFD(pSourceVO: FreeDataVO): FreeDataVO {
    let clone: FreeDataVO = null;
    if (pSourceVO) {
      clone = this.newFreedataVO(cloneDeep(pSourceVO.source));
    }
    return clone;
  }

  /**
   * Clonage d'un FreeDataValueVO
   * @param pSourceVO L'objet source
   */
  public cloneFDV(pSourceVO: FreedataValueVO): FreedataValueVO {
    let clone: FreedataValueVO = null;
    if (pSourceVO) {
      clone = this.newFreedataValueVO(cloneDeep(pSourceVO.source));
    }
    return clone;
  }

  /**
   * utilisé par le formulaire
   * alimente le FDV (un clone), pour le champ passé en fieldCode, avec la value
   * @param fdv 
   * @param fieldCode
   * @param value 
   */
  public feedFreedataValueField(fdv: FreedataValueVO, fieldCode: string, value: string): void {
    if (this.model.freedataEngine.basicFieldSet().has(fieldCode)) { // c'est un champ de base
      switch(this.model.freedataEngine.basicFieldSet().get(fieldCode).datakind) {
        case FIELD_KIND.STRING :
        case FIELD_KIND.BINARY :
          fdv[fieldCode] = value;
          break;
        case FIELD_KIND.BOOLEAN :
          fdv[fieldCode] = value === '1';
          break;
        case FIELD_KIND.NUMBER :
          fdv[fieldCode] = Number(value) || null;
          break;
        case FIELD_KIND.DATE :
          fdv[fieldCode] = value?.toString() || null;
          break;
        
        
          // case FIELD_KIND.TRS_LABEL :
          // // let lblVO: TranslatableLabelVO = null;
          // if (fieldCode === WizConstants.FIELD_LABEL) {
          //   fdv.label = JSON.parse(value);
          //   // fdv.labelVO = null;
          //   // lblVO = fdv.labelVO;
          // } else if (fieldCode === WizConstants.FIELD_DESCRIPTION) {
          //   // lblVO = fdv.descriptionVO;
          //   fdv.description = JSON.parse(value);
          // }
          // // if (!!lblVO) {
          // //   const entryvo: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
          // //   entryvo.languageCode = this.model.currentLocaleCode;
          // //   entryvo.label = value;
          // //   lblVO.updateTranslationEntry(entryvo);
          // // }
          // break;
        case FIELD_KIND.TRS_LABEL :
          let lblVO: TranslatableLabelVO = null;
          if (fieldCode === WizConstants.FIELD_LABEL) {
            lblVO = fdv.labelVO;
          } else if (fieldCode === WizConstants.FIELD_DESCRIPTION) {
            lblVO = fdv.descriptionVO;
          }
          if (!!lblVO) {
            const entryvo: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
            entryvo.languageCode = this.model.currentLocaleCode;
            entryvo.label = value;
            lblVO.updateTranslationEntry(entryvo);
          }
          break;
      }
    } else {
      this.addArgOnFDV(fdv, fieldCode, value);
      // fdv.addArg(fieldCode, value);
    }
    fdv.resetArgVO(); // pour refresh des valeurs des args (kill les VOs)
  }

  /**
   * ajout d'un arg sur un FDV
   * @param fdv 
   * @param fieldCode 
   * @param value 
   */
  private addArgOnFDV(fdv: FreedataValueVO, fieldCode: string, value: string): void {
    // console.log('addArgOnFDV', fdv, fieldCode, value);
    if (!fdv.moreArgsJson) {
        fdv.moreArgsJson = {};
      }

      fdv.moreArgsJson[fieldCode] = value;
      fdv.trueArgsObj = fdv.moreArgsJson;
  }

  /* ################################################
                      gestion des FDVs
    ################################################ */


  /**
   * création d'un freedatavalue (depuis l'arbre) ou depuis le formulaire
   * @param freedata FD parent
   * @param parent FDV parent (level précédent)
   * @param setCode générer le shortCode et le libellé
   * @returns le fdv
   */
  public createFreeDataValue(
    freedata: FreeDataVO,
    parent: FreedataValueVO,
    setCode: boolean = true
  ): FreedataValueVO {
    const fdv = this.newFreedataValueVO(null);
    fdv.freeDataCode = freedata.code;
    fdv.businessUnitCode = freedata.businessUnitCode;
    fdv.domain = freedata.domain;
    fdv.fdid = freedata.id;
    if (parent) {
      fdv.parentCode = parent.code;
      fdv.level = parent.level + 1;
      fdv.startDate = parent.startDate;
      fdv.endDate = parent.endDate;
    } else {
      fdv.level = 1;
      fdv.startDate = freedata.startDate;
      fdv.endDate = freedata.endDate;
    }

    const shortCodeSet: Set<string> = new Set<string>(); // création de l'incrément sur le code court
    for (const vo of freedata.getValuesVO()) {
      if (
        vo.parentCode === fdv.parentCode ||
        (WizStringUtils.isEmpty(vo.parentCode) && WizStringUtils.isEmpty(fdv.parentCode))
      ) {
        shortCodeSet.add(vo.shortCode);
      }
    }
    let shortCodeExists: boolean = true;
    let index: number = 1;
    let parentName: string;// = parent ? parent.shortCode : freedata.shortCode;
    if(parent) {
      if(parent.externalCode) {
        parentName = parent.externalCode;
      } else {
        parentName = parent.shortCode;
      }
    } else {
      if(freedata.externalCode) {
        parentName = freedata.externalCode;
      } else {
        parentName = freedata.shortCode;
      }
    }

    let shortCode: string = parentName + ViewData.NICE_KEY_SEPARATOR + index.toString();
    while (shortCodeExists) {
      shortCodeExists = shortCodeSet.has(shortCode); // unicité sur le code (si on a changé le shortCode)
      if (shortCodeExists) {
        shortCode = parentName + ViewData.NICE_KEY_SEPARATOR + (++index).toString();
      }
    }

    // interception du #NONE# sur le externalCode du FD
    if(!parent && freedata.externalCode === ViewData.NONE) {
      shortCode = (index).toString()
    }

    fdv.shortCode = shortCode; // alimentation du shortCode par défaut

    let codeExist: boolean = true;
    index = 100;
    // const parentCode: string = parent ? parent.code : ''; // freedata.code;
    const parts: string[] = [];
    if (!!parent) {
      parts.push(parent.code);
    }
    let newCode: string = parts.concat(index.toString()).join(ViewData.NICE_KEY_SEPARATOR);
    while (codeExist) {
      codeExist = freedata.getFreeDataValue(newCode) != null; // unicité sur le code (si on a changé le shortCode)
      if (codeExist) {
        newCode = parts.concat((++index).toString()).join(ViewData.NICE_KEY_SEPARATOR);
      }
    }

    fdv.code = newCode; // alimentation du code unique, Parent + shortCode

    // Libellés
    if (setCode) {
      let entry: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
      entry.languageCode = this.model.buLocaleCode;
      entry.label = ""; // fdv.shortCode;
      fdv.labelVO.addEntry(entry);
      const currentLanguage: string = this.model.currentLocaleCode;
      if (currentLanguage !== entry.languageCode) {
        entry = new TranslatableLabelEntryVO();
        entry.languageCode = currentLanguage;
        entry.label = ""; // fdv.shortCode;
        fdv.labelVO.addEntry(entry);
      }
    }
    // fdv.labelVO.initLabelEntriesVO();
    return fdv;
  }


  /**
   * Suppression d'un élément (avec ses enfants)
   * @param freedata le clone de travail
   * @param toDelete Elément à supprimer
   */
  public deleteFDV(freedata: FreeDataVO, toDelete: FreedataValueVO): void {
    const fdvMap: Map<string, FreedataValueVO[]> = new Map<string, FreedataValueVO[]>();
    freedata.getValuesVO().reduce((map: Map<string, FreedataValueVO[]>, value: FreedataValueVO) => {
      if (value.parentCode) {
        if (!map.has(value.parentCode)) {
          map.set(value.parentCode, []);
        }
        map.get(value.parentCode).push(value);
      }
      return map;
    }, fdvMap);
    this.recurseDeleteFDV(freedata, toDelete, fdvMap);
  }

  /**
   * Suppression récursive d'éléments de codification
   * @param freedata Codification
   * @param toDelete Elément à supprimer
   */
  public recurseDeleteFDV(
    freedata: FreeDataVO,
    toDelete: FreedataValueVO,
    fdvMap: Map<string, FreedataValueVO[]>
  ): void {
    if (fdvMap.has(toDelete.code)) {
      // d'abord suppression récursive des enfants
      fdvMap.get(toDelete.code).forEach((child) => {
        this.recurseDeleteFDV(freedata, child, fdvMap);
      });
    }
    freedata.removeFreeDataValue(toDelete); // puis suppression de la cible
  }

  /**
   * génération des creates / updates / deletes sur les baselines asservies à un FDV
   * @param FD source qui porte sa updateMap pour les FDVs
   * @returns une collection d'UpdateEntry[]
   */
  public generateSlaveBaselines(fd: FreeDataVO, widget: IFreedataWidget): UpdateEntry[] {
    let result: UpdateEntry[] = [];
    let moreUpdates: UpdateEntry[] = []; // les triggers & linkedRef

    let keys: VisionKey[] = widget.vision.stepKeyByLevel.get(widget.visionStep.level);
  
    // map de correspondance : clé de vision = code du arg
    let keyMap: Map<string, string> = new Map(keys.map(item => [item.code, null]));

    // correspondance sur le FD
    let baseKey: string = [fd.code, widget.level].join(WizConstants.CONTEXT_SEPARATOR);
    if(keyMap.has(baseKey)) {
      keyMap.set(baseKey, WizConstants.FIELD_CODE); 
    }

    // prise de l'ensemble des clés objets sur le FD
    fd.getArgsVO().forEach((arg: FreedataArgsVO) => {
      // console.log('arg', arg);  
      if (arg.dataType === FIELD_KIND.OBJECT) {
        if(keyMap.has(arg.datas)) {
          keyMap.set(arg.datas, arg.code); 
        }
      }
    });

    let wlMap: Map<string, WidgetLine> = new Map();

    // un seul FD, en fait
    [...fd.updateMap.values()].forEach((pair: any) => { // [FreedataValueVO, string]
      let fdv: FreedataValueVO = pair[0];
      let op: string = pair[1];
      let blKey: string = this.getKeyForFDV(fdv, keys, keyMap);
      let wl: WidgetLine = widget.fei.getWidgetLineForKey(widget.fei.scenario, blKey);
      

      // console.log('generateSlaveBaselines', blKey, wl, op);
      let update: UpdateEntry = null;
      // gestion des deletes
      switch(op) {
        case EM_ACTION.DELETE:
          if(wl) {
            wl.bl.operation = EM_ACTION.DELETE;
            update = this.model.dataEngine.generateUpdate(widget.fei, wl, null, null);
            update.baseline.deleteCascade = true;
            update.baseline.cascadeDeep = this.model.dataEngine.getMaxDeep(widget.fei.visionStepLevel);
            result.push(update);
          }
        break;

        case EM_ACTION.UPDATE:
          // console.log('generateslave baseline UPDATE');
          // prendre le FDV originel sur le FD
          // rechercher la wl originelle
          // elle doit exister, sinon create
          // passer le param cascadeUpdate
          // la collection de nouvelle clé
          // et le cascadeDeep pour le retour
          let realFDV: FreedataValueVO = fd.getFreeDataValue(fdv.code);
          // let realFDV: FreedataValueVO = (widget as any).currentFDV;
          let realBlKey: string = this.getKeyForFDV(realFDV, keys, keyMap);
          let realWL = widget.fei.getWidgetLineForKey(widget.fei.scenario, realBlKey); 
          // console.log('generateSlaveBaselines update', blKey, wl, realWL);
          if(!!realWL) { 
            wlMap.set(realWL.key, realWL); // ajout à la map pour play des linkedValues
            realWL.bl.operation = EM_ACTION.UPDATE;
            // TODO ici passer le clone pour jouer les triggers sur la WL
            // TODO jouer les valeurs par défaut & les triggers si origine de modif dans le trigger
            // préserver les valeurs

            let mainKey: string = [widget.freedataCode, widget.level].join(WizConstants.CONTEXT_SEPARATOR);
            
            // changement de structure de clé
            if(blKey !== realWL.key) {
              // console.log('generateSlaveBaselines update', blKey, wl, realWL, mainKey);
              update = this.model.dataEngine.generateUpdate(widget.fei, realWL, null, null);
              update.baseline.updateCascade = true;
              update.baseline.cascadeDeep = this.model.dataEngine.getMaxDeep(widget.fei.visionStepLevel);
              update.baseline.updateCascadeKey = blKey.split(WizConstants.KEY_SEPARATOR);
              result.push(update);
            }

            
            // LMA ajout des triggers
            // la keyMap sert à rien ici
            let myMap: Map<string, string> = new Map();
            // si le trigger correspond au FD#Level, on joue les modifs
            // générer les updates sur la BL
            let upds: UpdateEntry[] = this.feedDefaultValues(wl, widget.fei, myMap, mainKey);

            upds?.forEach(upe => {
              if(upe.oldValue !== upe.newValue) {
                result.push(upe);
              }
            });
            
            // possible faux positif, si la structure n'a pas changée
            // if(blKey === realWL.key && !upds) { // aucun changement, pas d'update
            //   // console.log('generateSlaveBaselines same key',realWL.key, blKey);
            // } else {
            //   result.push(update);
            // }     
            
            break;
          } // sinon, simplement pas de break, on va jouer un create
        case EM_ACTION.CREATE:
          // console.log('CREATE');
          // console.log('generateSlaveBaselines create', blKey, wl);
          if(!wl) { // dois PAS exister
            let thisKeyMap: Map<string, string> = new Map();
            let blKeySplitted: string[] = blKey.split(WizConstants.KEY_SEPARATOR);
            for (let index = 0; index < keys.length; index++) {
              thisKeyMap.set(keys[index].code, blKeySplitted[index])
            }
            let candidateBaseline: Baseline = this.model.widgetEngine.buildBaselineFromPrototype(widget.fei, thisKeyMap);
            
            wl = this.model.widgetEngine.buildWidgetLinePrototype(widget.fei, candidateBaseline);
            wlMap.set(wl.key, wl); // ajout à la map


            // console.log('generateSlaveBaselines create', blKey, wl);
            update = this.model.dataEngine.generateUpdate(widget.fei, wl, null, null);
            result.push(update);
            // console.log('generateSlaveBaselines create', result, wl);
            
            // LMA ajout des triggers
            // la keyMap sert à rien ici
            let myMap: Map<string, string> = new Map();
            let mainKey: string = [widget.freedataCode, widget.level].join(WizConstants.CONTEXT_SEPARATOR);
            // si le trigger correspond au FD#Level, on joue les modifs
            // générer les updates sur la BL
            let upds: UpdateEntry[] = this.feedDefaultValues(wl, widget.fei, thisKeyMap, mainKey);
            // console.log('generateSlaveBaselines create feedDefaultValues', result, wl);
            // TODO ici jouer les linkedRef
          }
        break;
      }
    });

    return result;
  }

  /**
   * construit la clé de baseline qui correspondent à un FDV
   * @param fdv cible
   * @param keys clés de la vision pour le step
   * @param keyMap mapping clé du step = code du arg
   * @returns la clé, scenario MAIN
   */
  private getKeyForFDV(fdv: FreedataValueVO, keys: VisionKey[], keyMap: Map<string, string>): string {
    let result: string = null;

    // prise des ancêtres
    let ancestorParts: Map<string, string> = new Map();

    // correspondance sur le FD
    let currentFDV: FreedataValueVO = fdv;
    let FD: FreeDataVO = fdv.getParentFDVO();
    while(currentFDV?.level >= 1) {
      let baseKey: string = [currentFDV.freeDataCode, currentFDV.level].join(WizConstants.CONTEXT_SEPARATOR);
      ancestorParts.set(baseKey, currentFDV.code); 
      currentFDV = FD.getFreeDataValue(currentFDV.parentCode);
    }


    let wlKeyPart: string[] = [];
    keys.forEach((vk: VisionKey) => {
      let part: string;
      if(ancestorParts.has(vk.code)) { // élément de structure de base du FDV
        part = ancestorParts.get(vk.code);
        
      } else {
        if(this.basicFieldSet().has(keyMap.get(vk.code))) { // champ de base sur le FDV (typiquement, le code du FDV en cours)
          part = fdv[keyMap.get(vk.code)];
        } else {
          part =  fdv.trueArgsObj[keyMap.get(vk.code)];
        }
  
        if(!!part && keyMap.get(vk.code)!== WizConstants.FIELD_CODE) { // on est sur un subFD, form FD#value
          part = part.split(WizConstants.CONTEXT_SEPARATOR)[1];
        }
      }

      // prise du level de clone, pour recherche de valeurs héritées
      wlKeyPart.push(part);
    });

    // console.log('wlKeyPart', wlKeyPart);
    result = wlKeyPart.join(WizConstants.KEY_SEPARATOR);

    return result;
  }

  /* ################################################
                Save (create / update)
    ################################################ */



  /**
   * enregistrement d'une codification
   * @param to le FD à créer
   * @param callback retour dans la vue
   */
  public create(to: FreeDataVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.createEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * delete d'une codification
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: FreeDataVO, callback: Function): void {
    // on flag le VO pour gestion correcte sur retour de service
    to.toDie = true;
    to.source.freeDataValues = {}; // on enlève les FDVs
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD sur CREATE et DELETE. Le reste est géré dans le composite
   * Le retour sera toujours le FreeData porteur.
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
      return;
    }

    // le VO source (le clone)
    const vo: FreeDataVO = cmd.sourceVO[0] as FreeDataVO;
    let realVO: FreeDataVO; // l'objet FD véritable
    // la liste d'erreurs métier
    const errors: BusinessException[] = cmd.result.errors;
    // le statut du pack
    const packStatus: string = cmd.result.status;
    if (packStatus === WIZ_COMMAND_STATE.FAULT) {
      errors.forEach((be: BusinessException) => {
        WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
      });
      return;
    }

    cmd.result.entries.forEach((empe: EMPackEntry) => {
      // TODO TODO update ??
      if (empe.operation === EM_ACTION.CREATE) {
        // existe pas, c'est un create (pas de FD sur create)
        const source: any = empe.entity;
        vo.source = source;
        this.model.freedataEngine.getFreeDataCollection().push(vo);
        this.model.freedataEngine.getFreedataMap().set(vo.code, vo);
        realVO = this.model.freedataEngine.getFreedataMap().get(vo.code); // l'objet véritable (pas le clone)
        // mise à jour du cache local
        this.model.freedataSyncService.addLocalFreedata(realVO);

      } else if (empe.operation === EM_ACTION.UPDATE) {
        console.log('TODO TODO check FDV', vo);
        // const source: any = empe.entity;
        // vo.source = source;
        // this.model.freedataEngine.getFreedataMap().set(vo.code, vo);

      } else if (empe.operation === EM_ACTION.DELETE) {
        const source: any = empe.entity; // Ivo en réalité
        realVO = this.model.freedataEngine.getFreedataMap().get(vo.code); // l'objet véritable (pas le clone)
        this.model.freedataEngine.getFreedataMap().delete(realVO.code);
        this.model.freedataEngine.getFreeDataCollection().splice(this.model.freedataEngine.getFreeDataCollection().indexOf(realVO), 1);

        // mise à jour du cache local
        this.model.freedataSyncService.removeLocalFreedata(realVO);
      }
      realVO.operation = empe.operation; // on fait porter l'opération C  D ou
    });

    // console.log ('merge FD / FDV', cmd.result.entries);
    cmd.viewCallBack(realVO, packStatus); // retour en vue
  }

  /** création d'un nouveau FD  */
  public createFD(): FreeDataVO {
    const fdVO = this.newFreedataVO(null);
    // fdVO.isFake = true;
    fdVO.chronoCode = ViewData.defaultChronoCode;
    fdVO.domain = this.model.domainCode;
    fdVO.businessUnitCode = this.model.businessUnitCode;
    fdVO.kindMeasure = false;
    fdVO.kindWild = false;
    this.model.currentEditedFreedata = fdVO;
    return fdVO;
  }

  /* ################################################
              gestion des args
  ################################################ */

  /** création d'un nouveau arg  */
  public createArg(clone: FreeDataVO): FreedataArgsVO {
    const vo =this.newFreedataArgsVO();
    vo.domain = clone.domain;
    vo.businessUnitCode = clone.businessUnitCode;
    vo.freeDataCode = clone.code;
    vo.uid = WizMathUtils.createUUID();
    return vo;
  }

  // ajout d'un arg sur la collection du FD
  public addArg(clone: FreeDataVO, arg: FreedataArgsVO): FreedataArgsVO {
    clone.addArg(arg);
    return arg;
  }

  // suppression d'un arg de la collection du FD
  public deleteArg(clone: FreeDataVO, arg: FreedataArgsVO): FreedataArgsVO {
    clone.removeArg(arg);
    return arg;
  }

  /**
   * accesseur pour le formatage des valeurs dans un filter.pop.grid
   * @param params le param de la cellule
   * @returns rendu formaté
   */
  public fdArgFastFormat(fdv: FreedataValueVO, argCode: string): any {
    let result = '';
    const arg: FreedataArgsVO = this.getArgsFromFD(fdv.freeDataCode).find((item: FreedataArgsVO) => item.code === argCode);
    if (arg) {
      result = this.model.formatEngine.applyFormat(fdv.argsObjVO[argCode], arg.dataType, arg.format, null); // et formater
      // console.log('fd format', params.value, result);
    }
    return result;
  }

  /** 
   * formatage d'un champ / sous champ de FDV
   *
   * @param key le code unique FD#FDV
   * @param arg le champ args
   */
  public universalFormat(key: string, arg: string): any {
    // TODO mémoriser libellé sur arg en plus de son code passer sur un objet arg{code, label/ value}
    const parts = key.split(ViewData.CONTEXT_SEPARATOR);
    const fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(parts[0], parts[1]);
    if (fdv) {
      return fdv.argsObjVO[arg]; // ça, c'est brut...
    }
    // console.log('universalFormat');
  }

  /** joli message d'erreur */
  public niceErrorMessage(kind: string, code: string, label: string, fdv?: FreedataValueVO): void {
    let mess: string = WizTranslationManager.getInstance().translateKey(kind);
    mess = mess + ':' + '<br>';
    mess += code + ' ' + label+ '<br>';
    if(!!fdv) {
      mess += fdv.source.createBy; //  + ' ' + fdv.source.updateTime; // TODO format correct pour heures (WizDateUtils.numberToStringDate(emt.date, WizDateUtils.FULL_FORMAT_MASK))
      const date: string = WizDateUtils.numberToLocalizedString(fdv.source.createTime);
      mess += ' ' + date;
    }
    WizPopUp.toast(mess, WizIcon.TOAST_WARNING, 5000, true, false, "250px", "100px").show();
  }


  /**
   * construction des triggers sur form ou fdForm.slave
   * TODO TODO créer le reverseTriggersMap
   * @param fei 
   * @returns Map<string, Triggers>
   */
  public buildTriggers(fei: FeiVO): [Map<string, Triggers>, Map<string, Set<Triggers>>]
  {
    let triggersMap = new Map();
    let reverseTriggersMap: Map<string, Set<Triggers>> = new Map();
    fei.editableKeys.forEach((fkd: FeiKeyDef) => {
      let triggers: Set<string> = new Set();
      let defaultValue: string = null;
      if(fkd.isFreedata) { // c'est une filterKey
        let freedata: FreeDataVO = this.model.freedataEngine.getFreeData(fkd.code); // là, on veut juste le code
        defaultValue = freedata?.defaultValue;
        if(!!defaultValue) {
          if(!!freedata.defaultTrigger) {
            triggers = new Set(freedata.defaultTrigger.split(ViewData.KEY_SEPARATOR));
          }
          triggersMap.set(fkd.fullCode, { fkd, defaultValue, triggers }); // mais on mémorise par code#level
        }
      } else if(fkd.isMeasure) { // defaultValue et triggerValue sur les mesures
        let measure: Measure = fkd.measure;
        if(measure.defaultValue) { // alimentation des valeurs par défaut, avec ou sans trigger
          let fakeDefaultValueKey: string = [WizConstants.FAKE_DEFAULT_VALUE, measure.shortCode].join(ViewData.CODE_LEVEL_SEPARATOR);
          const mm: MeasMode = MeasureCoreEngine.LightFakeMeasureMode(
            fei,
            fakeDefaultValueKey,
            WizConstants.MEASURE_MODE_SUBMIT,
            measure.defaultValue,
            measure.dataKind
          );

            if(!!mm.deps) {
            [...mm.deps.values()].forEach(s => {
              triggers.add(s);
            })
          }
          triggersMap.set(fakeDefaultValueKey, { fkd, defaultValue: measure.defaultValue, mm, triggers }); // mémorisation du trigger
        }
        if(measure.triggerValue) {
          let fakeTriggerValueKey: string = [WizConstants.FAKE_TRIGGER, measure.shortCode].join(ViewData.CODE_LEVEL_SEPARATOR);
          const mm: MeasMode = MeasureCoreEngine.LightFakeMeasureMode(
            fei,
            fakeTriggerValueKey,
            WizConstants.MEASURE_MODE_SUBMIT,
            measure.defaultValue,
            measure.dataKind
          );//measure.triggerValue);
            
            let trig: Triggers = { fkd, defaultValue: measure.defaultValue, mm, triggers };

            let trigParts: string[] = measure.triggerValue?.split(WizConstants.KEY_SEPARATOR);
              trigParts.forEach(t => {
                triggers.add(t);
                if(reverseTriggersMap.has(t)) {
                  reverseTriggersMap.get(t).add(trig);
                }
                else {
                  reverseTriggersMap.set(t, new Set([trig]));
                }
              });
            trig.triggers = triggers;
            triggersMap.set(fakeTriggerValueKey, trig); // mémorisation du trigger
        }
      }

    });

    return [triggersMap, reverseTriggersMap];
  }


  /** 
   * Cascade trigger / default keys (cf feedDefaultKeys in FormWidget)
   * on passe le code FD pour jouer les updates:
   * - si la BL existe
   * - que le trigger correspond au FD#level
   * - on joue l'update
   * @param wl 
   * @param fei 
   * @param keyPartMap 
   * @param fdCodeLevel 
   */
  private feedDefaultValues(
    wl: WidgetLine,
    fei: FeiVO,
    keyPartMap: Map<string, string>,
    fdCodeLevel?: string
  ): UpdateEntry[] {

    let result: UpdateEntry[] = [];
    let played: Set<string> = new Set();

    let raw: any[] = this.buildTriggers(fei);
    let triggersMap = raw[0];
    // TODO TODO harmoniser avec form.widget
    let fakeFei = this.model.dataEngine.generateFakeFEI(fei) as FeiVO;  
    fakeFei.fakeFDV = fei.fakeFDV;
 
    // console.log('slave feedDefaultValues', triggersMap);
    
    [...triggersMap.keys()].forEach((trigKey: string) => {
      let formTrig: Triggers = triggersMap.get(trigKey);
      
      if (formTrig.defaultValue !== null) {

        // si n'existe pas OU si trigger sur FD#level
        if(!wl.bl.id || (wl.bl.id && !!fdCodeLevel && formTrig.triggers.has(fdCodeLevel))) {
          // console.log('trigKey', trigKey, formTrig.defaultValue, keyPartMap);
          if(formTrig.fkd.isFreedata) { // trigger issus du Freedata
            //TODO modif sur filterKey, va pas être pris en compte
            const triggeredValue: string = this.model.widgetEngine.computeDefaultValue(fakeFei, wl, formTrig.fkd.fullCode);
            keyPartMap.set(formTrig.fkd.fullCode, triggeredValue);
            // console.log('trigKey', trigKey, formTrig.fkd.fullCode, triggeredValue);
          } else if(formTrig.fkd.isMeasure) {//} && firstInit) {// && !trigKey.startsWith(WizConstants.FAKE_TRIGGER)) { // defaultValue sur une mesure (on ne joue pas les triggers)

            if(!played.has(formTrig.fkd.fullCode)) {
              played.add(formTrig.fkd.fullCode);
              // ne jouer qu'une fois. triggerMap contient les default à vide ET les triggers (possible doublon)
              if(!wl.bl.id || trigKey.startsWith(WizConstants.FAKE_TRIGGER)) {
                const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(formTrig.mm.mmi, wl, fakeFei, true, keyPartMap);
                
                let realValor: string = null;
                try {
                  if(cr?.valor !== null && cr?.valor !== undefined) {
                    realValor = cr.valor.toString();
                  }
                } catch(e: any) {
                  console.log('cast valor mismatch', cr, e);
                }

                // console.log('trigValue', trigKey, formTrig.fkd.fullCode, cr.valor);
                // if(trigKey === "@@TRIG_OAM_PER-SEM") {
                //   console.log('TRIG_OAM_PER', this.keyPartMap, cr?.valor);
                // }
                // console.log('trigKey', trigKey, keyPartMap, realValor);
                if(realValor?.length > 0 || !!wl.bl.id) { // retour à null possible par le trigger
                  let ue: UpdateEntry = this.model.dataEngine.generateUpdate(fei, wl, formTrig.fkd, realValor);
                  if (ue.oldValue !== ue.newValue) {
                    result.push(ue);
                  }
                }   
              }
            }
            
          }    
        }


      }
    });

    // console.log('FDEngine feedDefaultValues', result);

    let moreUpdates: UpdateEntry[] = []; // les triggers & linkedRef

    if(!!result) {

      // TODO pas comme ça. Sur UPDATE et SUR CREATE (pas de updEntry sur CREATE au détail)
      result.forEach((upd: UpdateEntry) => {
        // TODO gérer les linkedRef multiples (plusieurs FD)
        let fkd: FeiKeyDef = fei.keyMap.get(upd.field);
        let meas: Measure = fkd?.measure;
        if(wl && meas && !!meas.linkedRef) { // gestion du crossDomain
          let oneMorOrMany: UpdateEntry[] =  this.model.widgetEngine.resolveCrossed(fei, null, upd, result, wl, fkd);
          if(!!oneMorOrMany) {    
            oneMorOrMany.forEach(omom => {
              let linkedFKD: FeiKeyDef = fei.keyMap.get(omom.field);
              if(linkedFKD.isFilterKey) {
                // alimentation directement sur la baseline
                BaselineEngine.baselineSetFilterKeyValue(upd.baseline, omom.field, omom.newValue);
                // console.log('linkedFKD filter', linkedFKD, fkd.fullCode, oneMore.newValue, upd.baseline );
              }
              moreUpdates.push(omom);
            });
          }  
        }
      });
    }

    // console.log('FDEngine feedDefaultValues more', moreUpdates);
    if(moreUpdates.length > 0) {
      // console.log('feedDefaultValues moreUpdate', moreUpdates);
      result = [...result, ...moreUpdates];
    }

    return result;
  }

  /**
   * calcul des champs formule sur FDV
   * attention TODO TODO gérer le cascade ?
   * @param fdCode 
   * @param pool 
   */
  public computeFormulasOnFDV(fdCode: string, pool: FreedataValueVO[]): void {
    
    // prise du FD
    let fd: FreeDataVO = this.getFreeData(fdCode);
    
    if(!fd) {
      return; // le FD n'existe pas (mauvaise reprise de données)
    }
    
    /** 
     * la matrice de résolution des dépendances (triggers) pour jouer les cascades
     * code source = TriggerMatrixItem. Les children portent les éléments à rejouer en cascade
     */
    // let matrix: Map<string, TriggerMatrixItem> = null;
    // alimentation de la map de champs pour ce FD
    let fieldsMap = new Map(fd.allFieldsAsArgs.map(item => [item.code, item]));
    
    let fakeFei: FeiVO = null;
    let tmpFEI: FeiVO  = this.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, 1, ViewData.DEFAULT_SCENARIO) as FeiVO;
    if(!tmpFEI) {
      tmpFEI = this.model.dataEngine.generateFei(this.model.currentVision, 
        this.model.currentVision.visionSteps[1], 
        ViewData.DEFAULT_SCENARIO, null, true);
    }
    if(!!tmpFEI) {
      this.model.measureEngine.buildFeiMatrix(tmpFEI);
      fakeFei = this.model.dataEngine.generateFakeFEI(tmpFEI) as FeiVO;  
    }

    /** recalcul pour les éléments à recalculer (à chaque fois) */
    [...fieldsMap.keys()].forEach((key: string) => { // pour chaque champ
      const fdavo: FreedataArgsVO = fieldsMap.get(key);
      if(!!fdavo.valor && fdavo.valorTrigger?.indexOf(ViewData.TRIGGER_ALWAYS) > -1){// } && !this.clone.argsObjVO.hasOwnProperty(key)) { // seulement si une valeur par défaut et PAS instancié
        // on calcul pour chaque FDV
        pool.forEach(fdv => {
          let result: string = this.model.freedataFormulaCoreEngine.computeFreedataExp(fdv, fdavo.valor, fakeFei).valor || null;
          this.model.freedataEngine.feedFreedataValueField(fdv, key, result);
        });
      }
    });
    
  }

    /**
     * Return le libellé d'une valeur de type subFDV
     * @param key le code du FD#FDV_SHORTCODE
     * @param pCode Le code du FDV
     */
    public getSubFDV(key: string): string {
      let result: string = '';
      if(key) {
        try {
          let parts: string[] = key.split(WizConstants.CONTEXT_SEPARATOR);
          let fdv: FreedataValueVO = this.getFreedataValue(parts[0], parts[1]);
          result = fdv?.trsLabel;
        } catch(e:any) {

        }
      } 
      return result;
    }


}
